import React, { useState, useEffect, useRef, useCallback } from 'react';
import './styles.css';
import { perfMark, perfSend } from '../utils/perfTracker';
import { useV3StateContext } from './V3StateContext';
import V3LoadingScreen from './components/V3LoadingScreen';
import V3ErrorBoundary from './components/V3ErrorBoundary';
import V3ServerErrorScreen from './components/V3ServerErrorScreen';
import { useSettingsDialog } from '../contexts/SettingsDialogContext';
import useAuth from '../hooks/useAuth';
import { V3DataProvider } from '../contexts/V3DataContext';
import { useGlobalSSE } from '../contexts/GlobalSSEContext';
import { useDragScroll } from './hooks/useDragScroll';
import { useTelegramHaptic } from '../hooks/useTelegramHaptic';
import V3Header from './components/V3Header';
import V3MapContainer, { MAP_EDGE_PADDING } from './components/V3MapContainer';
import V3BottomNav from './components/V3BottomNav';
import { useMovementArrival } from './hooks/useMovementArrival';
import { useMapCentering } from './hooks/useMapCentering';
// Lazy load tabs — chunks download only when tab is first activated
const V3FeedTab = React.lazy(() => import('./V3FeedTabStructured'));
const V3HeroTab = React.lazy(() => import('./V3HeroTabStructured'));
const V3InventoryTab = React.lazy(() => import('./V3InventoryTab'));
const V3GuildTab = React.lazy(() => import('./V3GuildTabNew'));
// Lazy load dialogs — removes moment-timezone (~60KB gzip) from main bundle
const V3SettingsDialog = React.lazy(() => import('./components/V3SettingsDialog'));
const V3MarketView = React.lazy(() => import('./components/marketplace/V3MarketView'));
const V3AdministrationPanel = React.lazy(() => import('./components/administration/V3AdministrationPanel'));

function AppV3() {
    // Restore last active tab from localStorage, default to 'world' if not set
    const [activeTab, setActiveTab] = useState(() => {
        const savedTab = localStorage.getItem('v3_active_tab');
        return savedTab || 'world';
    });
    const v3State = useV3StateContext();
    const mapContainerRef = useRef(null);
    const regionInfoRef = useRef(null);
    const [mapPaddingTop] = useState(180 + MAP_EDGE_PADDING); // Base padding for region card + edge padding
    const [isRegionCardCollapsed, setIsRegionCardCollapsed] = useState(true);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isMarketplaceOpen, setIsMarketplaceOpen] = useState(false);
    const [isAdministrationOpen, setIsAdministrationOpen] = useState(false);
    const { open: isSettingsOpen, openDialog: openSettingsDialog, closeDialog: closeSettingsDialog } = useSettingsDialog();

    // Check if any overlay is open (to hide tab highlight)
    const isAnyOverlayOpen = isSettingsOpen || isMarketplaceOpen || isAdministrationOpen;
    const { token } = useAuth();
    const { setActiveTab: setGlobalActiveTab, connectionState } = useGlobalSSE();
    const { selectionChanged, impactOccurred, notificationOccurred, isAvailable } = useTelegramHaptic();

    // Map centering logic (center on player + auto-scroll on load)
    const { centerMapOnPlayer } = useMapCentering({
        mapContainerRef,
        position: v3State.position,
        mapPaddingTop,
        isLoading: v3State.loading,
        map: v3State.map,
        activeTab,
    });

    // Movement arrival detection (animation + haptic + map centering)
    const { playerArrivalCell } = useMovementArrival({
        movementProgress: v3State.regionCard?.movementProgress,
        position: v3State.position,
        haptic: { isAvailable, notificationOccurred },
        centerMapOnPlayer,
    });

    // Performance: mark when app renders with data, then preload non-critical images
    const perfSentRef = useRef(false);
    useEffect(() => {
        if (!v3State.loading && !perfSentRef.current) {
            perfSentRef.current = true;
            perfMark('appRendered');
            setTimeout(perfSend, 2000);
            // Preload common images after app is rendered — never during critical path
            import('./utils/globalImagePreloader').then(m => m.preloadCommonImages());
        }
    }, [v3State.loading]);

    // Clean up Telegram init info if present
    React.useEffect(() => {
        if (window.__telegramInitInfo) {
            delete window.__telegramInitInfo;
        }
    }, []);

    // Enable drag scroll for map container only when map is loaded
    useEffect(() => {
        if (!v3State.loading && v3State.map && mapContainerRef.current) {
            // Hook is already applied, but we need to ensure it's initialized after content loads
        }
    }, [v3State.loading, v3State.map]);

    // Only enable drag scroll on map when:
    // 1. We're on the world tab
    // 2. Region card is collapsed
    const enableMapDragScroll = activeTab === 'world' && isRegionCardCollapsed;
    useDragScroll(mapContainerRef, enableMapDragScroll);

    // Tab configuration for carousel
    const tabIndexMap = {
        world: 0,
        inventory: 1,
        hero: 2,
        guild: 3,
        feed: 4
    };

    // Calculate circle percentages with proper clamping
    const healthPercent = Math.min(1, (v3State.profile.health || 0) / Math.max(1, v3State.profile.maxHealth || 1));
    const manaPercent = Math.min(1, (v3State.profile.mana || 0) / Math.max(1, v3State.profile.maxMana || 1));
    const stepsPercent = Math.min(1, (v3State.profile.steps || 0) / Math.max(1, v3State.profile.maxSteps || 1));

    // Calculate stamina bar percentage
    const currentStamina = v3State.profile.stamina ?? 0;
    const currentStaminaCap = Math.max(1, v3State.profile.staminaCap || 1); // Avoid division by 0
    const staminaPercent = Math.max(0, Math.min(100, (currentStamina / currentStaminaCap) * 100));

    // Calculate stamina regen timer percentage when stamina is 0
    // 60 minutes = 0%, 0 minutes = 99%
    const staminaRegenPercent = currentStamina === 0 && v3State.profile.staminaRegenTime >= 0
        ? Math.max(1, Math.min(99, ((60 - v3State.profile.staminaRegenTime) / 60) * 100))
        : 0;

    const handleBurgerClick = useCallback(() => {
        setIsMenuOpen(prev => !prev);
    }, []);

    const handleCloseMenu = useCallback(() => {
        setIsMenuOpen(false);
    }, []);

    const handleTabChange = (newTab) => {
        // Close all overlays when switching tabs
        closeSettingsDialog();
        setIsAdministrationOpen(false);
        setIsMarketplaceOpen(false);

        if (newTab !== activeTab) {
            setActiveTab(newTab);
            // Save active tab to localStorage
            localStorage.setItem('v3_active_tab', newTab);

            // Center map on player when switching to world tab (instant, no animation)
            if (newTab === 'world') {
                // Small delay to ensure tab content is rendered
                setTimeout(() => {
                    centerMapOnPlayer(true);
                }, 100);
            }

            // Universal haptic feedback for all platforms
            if (isAvailable) {
                // Try both impact and selection for all platforms
                try {
                    impactOccurred('light');
                } catch (e) {
                    console.warn('[AppV3] impactOccurred failed:', e);
                    // Try selection as fallback
                    try {
                        selectionChanged();
                    } catch (e2) {
                        console.warn('[AppV3] selectionChanged failed:', e2);
                    }
                }
            }
        }
    };

    const handleSettings = () => {
        setIsMenuOpen(false);
        setIsAdministrationOpen(false);
        setIsMarketplaceOpen(false);
        openSettingsDialog();
    };

    const handleMarketplace = () => {
        setIsMenuOpen(false);
        closeSettingsDialog();
        setIsAdministrationOpen(false);
        setIsMarketplaceOpen(true);
    };

    const handleAdministration = () => {
        setIsMenuOpen(false);
        closeSettingsDialog();
        setIsMarketplaceOpen(false);
        setIsAdministrationOpen(true);
    };

    const handleToggleRegionCard = useCallback((newCollapsed) => {
        setIsRegionCardCollapsed(newCollapsed);
        // Smooth scroll to player position only when collapsing
        if (newCollapsed) {
            centerMapOnPlayer();
        }
    }, [centerMapOnPlayer]);

    // Send tab change to server using global SSE
    useEffect(() => {
        if (!token) return;

        // Map v3 tab names to server tab identifiers
        const v3TabMapping = {
            'world': 'world_v3',
            'inventory': 'v3_inventory',
            'hero': 'v3_hero',
            'guild': 'v3_guild',
            'feed': 'feed_v3'
        };

        const serverTabName = v3TabMapping[activeTab] || 'world_v3';

        // Update active tab in global SSE service (this also sends to server)
        setGlobalActiveTab(serverTabName);
    }, [activeTab, token, setGlobalActiveTab]);

    // Add viewport settings
    useEffect(() => {
        // Ensure viewport meta tag includes viewport-fit=cover for iOS safe areas
        let viewport = document.querySelector('meta[name="viewport"]');
        if (!viewport) {
            viewport = document.createElement('meta');
            viewport.name = 'viewport';
            document.head.appendChild(viewport);
        }
        viewport.content = 'width=device-width, initial-scale=1, viewport-fit=cover';
    }, []);

    // Show error state
    if (v3State.error) {
        return (
            <V3DataProvider>
                <div className="v3-app-wrapper">
                    <V3ServerErrorScreen />
                </div>
            </V3DataProvider>
        );
    }

    // Show loading state
    if (v3State.loading) {
        return (
            <V3DataProvider>
                <div className="v3-app-wrapper">
                    <V3LoadingScreen />
                </div>
            </V3DataProvider>
        );
    }

    return (
        <V3DataProvider>
            <div className="v3-app-wrapper">
            {/* Game Header */}
            <V3Header
                profile={v3State.profile}
                connectionState={connectionState}
                healthPercent={healthPercent}
                manaPercent={manaPercent}
                stepsPercent={stepsPercent}
                staminaPercent={staminaPercent}
                staminaRegenPercent={staminaRegenPercent}
                currentStamina={currentStamina}
                isMenuOpen={isMenuOpen}
                onBurgerClick={handleBurgerClick}
                onCloseMenu={handleCloseMenu}
                onSettings={handleSettings}
                onMarketplace={handleMarketplace}
                onAdministration={handleAdministration}
            />

            {/* Main Content Area */}
            <main className="content-area">
                <div className="carousel-container">
                    <div
                        className={`carousel-track ${activeTab}`}
                        style={{
                            transform: `translateX(-${tabIndexMap[activeTab] * 100}%)`,
                        }}
                    >
                        {/* World Tab Content */}
                        <div className="carousel-slide" data-tab="world">
                            <V3MapContainer
                                mapContainerRef={mapContainerRef}
                                mapPaddingTop={mapPaddingTop}
                                regionCard={v3State.regionCard}
                                regionInfoRef={regionInfoRef}
                                isRegionCardCollapsed={isRegionCardCollapsed}
                                onToggleRegionCard={handleToggleRegionCard}
                                map={v3State.map}
                                mapSize={v3State.mapSize}
                                playerArrivalCell={playerArrivalCell}
                            />
                        </div>

                        {/* Inventory Tab Content */}
                        <div className="carousel-slide" data-tab="inventory">
                            <V3ErrorBoundary fallbackMessage="Failed to load inventory data">
                                {activeTab === 'inventory' && (
                                    <React.Suspense fallback={null}>
                                        <V3InventoryTab />
                                    </React.Suspense>
                                )}
                            </V3ErrorBoundary>
                        </div>

                        {/* Hero Tab Content */}
                        <div className="carousel-slide" data-tab="hero">
                            <V3ErrorBoundary fallbackMessage="Failed to load hero data">
                                {activeTab === 'hero' && (
                                    <React.Suspense fallback={null}>
                                        <V3HeroTab />
                                    </React.Suspense>
                                )}
                            </V3ErrorBoundary>
                        </div>

                        {/* Guild Tab Content */}
                        <div className="carousel-slide" data-tab="guild">
                            <V3ErrorBoundary fallbackMessage="Failed to load guild data">
                                {activeTab === 'guild' && (
                                    <React.Suspense fallback={null}>
                                        <V3GuildTab />
                                    </React.Suspense>
                                )}
                            </V3ErrorBoundary>
                        </div>

                        {/* Feed Tab Content */}
                        <div className="carousel-slide" data-tab="feed">
                            {activeTab === 'feed' && (
                                <React.Suspense fallback={null}>
                                    <V3FeedTab activeTab={activeTab} />
                                </React.Suspense>
                            )}
                        </div>
                    </div>
                </div>
            </main>

            {/* Bottom Navigation */}
            <V3BottomNav
                activeTab={activeTab}
                isAnyOverlayOpen={isAnyOverlayOpen}
                isGM={v3State.profile.isGM}
                onTabChange={handleTabChange}
                onMarketplace={handleMarketplace}
                onSettings={handleSettings}
                onAdministration={handleAdministration}
            />

            {/* V3 Settings Dialog - Lazy loaded */}
            {isSettingsOpen && (
                <React.Suspense fallback={null}>
                    <V3SettingsDialog />
                </React.Suspense>
            )}

            {/* V3 Marketplace Dialog - Lazy loaded */}
            <React.Suspense fallback={<V3LoadingScreen />}>
                <V3MarketView
                    open={isMarketplaceOpen}
                    onClose={() => setIsMarketplaceOpen(false)}
                />
            </React.Suspense>

            {/* V3 Administration Panel - Lazy loaded, only for GMs */}
            {v3State.profile.isGM && (
                <React.Suspense fallback={<V3LoadingScreen />}>
                    <V3AdministrationPanel
                        open={isAdministrationOpen}
                        onClose={() => setIsAdministrationOpen(false)}
                    />
                </React.Suspense>
            )}

        </div>
        </V3DataProvider>
    );
}

export default AppV3;
